// src/api/website-api/web-pos-order.js
// POS Order API — dedicated endpoints for POS order management
import axios from "axios";

const POS_BASE = `${process.env.REACT_APP_API_POS_WITH_WEBSITE}/api/vendor`;

// ---------------------------------------------------------------------------
// Headers helper — JWT + Store-ID
// ---------------------------------------------------------------------------
const getHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    "";
  const storeId =
    localStorage.getItem("storeId") ||
    localStorage.getItem("store_id") ||
    "1";

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "Store-ID": storeId,
  };
};

// ---------------------------------------------------------------------------
// buildPosOrderPayload — maps POS cart state → API body
// ---------------------------------------------------------------------------
/**
 * @param {Object} opts
 * @param {Array}  opts.selectedItems   — cart items from POS
 * @param {Object} opts.customerInfo    — { firstname, lastname, telephone, email }
 * @param {number} opts.totalPayable    — final total after discount/tax
 * @param {number} opts.amountPaid      — amount the customer handed over / paid
 * @param {string} opts.paymentType     — "cash" | "knet" | "card" | "split"
 * @param {number} opts.discountAmount  — couponDiscount + item discounts
 * @param {number} opts.taxAmount       — total tax
 * @param {string} opts.notes           — optional order note
 * @param {Object|null} opts.couponData — coupon object (if applied)
 * @param {Array}  opts.paymentSplits   — split payment entries (only for "split")
 */
export const buildPosOrderPayload = ({
  selectedItems = [],
  customerInfo = {},
  totalPayable = 0,
  amountPaid = 0,
  paymentType = "cash",
  discountAmount = 0,
  taxAmount = 0,
  notes = "",
  couponData = null,
  paymentSplits = [],
}) => {
  const customerName = [
    customerInfo.firstname?.trim(),
    customerInfo.lastname?.trim(),
  ]
    .filter(Boolean)
    .join(" ") || "Walk-in Customer";

  const customerPhone = customerInfo.telephone?.trim() || "";

  const products = selectedItems.map((item) => ({
    product_id: Number(item._id || item.product_id || item.productId),
    quantity: Number(item.quantity || 1),
    discount: Number(item.itemDiscount || 0),
  }));

  const payload = {
    customer_name: customerName,
    customer_phone: customerPhone,
    payment_type: paymentType,
    amount_paid: Number(amountPaid) || Number(totalPayable) || 0,
    discount_amount: Number(discountAmount) || 0,
    tax_amount: Number(taxAmount) || 0,
    notes: notes || "",
    products,
  };

  // Coupon info
  if (couponData) {
    payload.coupon_id = couponData.coupon_id || couponData.id || null;
    payload.coupon_code = couponData.code || "";
  }

  // Split payment entries (only when payment_type === "split")
  if (paymentType === "split" && paymentSplits.length > 0) {
    payload.payment_splits = paymentSplits.map((s) => ({
      payment_type: s.payment_type || "cash",
      amount: Number(s.amount) || 0,
      reference: s.reference || "",
    }));
  }

  return payload;
};

// ---------------------------------------------------------------------------
// createPosOrder
// ---------------------------------------------------------------------------
/**
 * POST /api/orders/create/
 * @param {Object} orderData — from buildPosOrderPayload
 * @returns {Promise<Object>}
 */
export const createPosOrder = async (orderData) => {
  console.log("📤 POS create order payload:", orderData);

  const res = await axios.post(`${POS_BASE}/orders/create/`, orderData, {
    headers: getHeaders(),
  });

  return res.data;
};

// ---------------------------------------------------------------------------
// listPosOrders
// ---------------------------------------------------------------------------
/**
 * GET /api/orders/?...filters
 * @param {Object} filters — { cashier_id, session_id, date_from, date_to,
 *                              payment_type, order_status, page, page_size }
 * @returns {Promise<Object>}
 */
export const listPosOrders = async (filters = {}) => {
  const params = {};
  if (filters.cashier_id) params.cashier_id = filters.cashier_id;
  if (filters.session_id) params.session_id = filters.session_id;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.payment_type) params.payment_type = filters.payment_type;
  if (filters.order_status) params.order_status = filters.order_status;
  if (filters.page) params.page = filters.page;
  if (filters.page_size) params.page_size = filters.page_size;

  const res = await axios.get(`${POS_BASE}/orders/`, {
    headers: getHeaders(),
    params,
  });

  return res.data;
};

// ---------------------------------------------------------------------------
// getPosOrderById
// ---------------------------------------------------------------------------
/**
 * GET /api/orders/:id/
 * @param {number|string} orderId
 * @returns {Promise<Object>}
 */
export const getPosOrderById = async (orderId) => {
  const res = await axios.get(`${POS_BASE}/orders/${orderId}/`, {
    headers: getHeaders(),
  });

  return res.data;
};

// ---------------------------------------------------------------------------
// voidPosOrder
// ---------------------------------------------------------------------------
/**
 * PUT /api/orders/:id/  body: { action: "void" }
 * @param {number|string} orderId
 * @returns {Promise<Object>}
 */
export const voidPosOrder = async (orderId) => {
  const res = await axios.put(
    `${POS_BASE}/orders/${orderId}/`,
    { action: "void" },
    { headers: getHeaders() }
  );

  return res.data;
};

// ---------------------------------------------------------------------------
// refundPosOrder
// ---------------------------------------------------------------------------
/**
 * PUT /api/orders/:id/  body: { action: "refund" }
 * @param {number|string} orderId
 * @returns {Promise<Object>}
 */
export const refundPosOrder = async (orderId) => {
  const res = await axios.put(
    `${POS_BASE}/orders/${orderId}/`,
    { action: "refund" },
    { headers: getHeaders() }
  );

  return res.data;
};
